import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import type { AstrologerProfile } from '../../types/professional';
import { PRO_SERVICES } from '../../types/professional';

export function AstrologerPublicPage() {
  const { slug } = useParams<{ slug: string }>();
  const [ap, setAp]         = useState<AstrologerProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    supabase
      .from('astrologer_profiles')
      .select('*')
      .eq('slug', slug)
      .eq('is_public', true)
      .eq('is_active', true)
      .maybeSingle()
      .then(({ data }) => {
        if (!data) setNotFound(true);
        else setAp(data as AstrologerProfile);
        setLoading(false);
      });
  }, [slug]);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-base)' }}>
        <p style={{ color: 'var(--text-muted)' }}>Carregando perfil…</p>
      </div>
    );
  }

  if (notFound || !ap) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-base)', gap: '1rem' }}>
        <p style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', color: 'var(--petrol)' }}>✦</p>
        <p style={{ color: 'var(--text-primary)', fontSize: '1.1rem', fontWeight: 600 }}>Perfil não encontrado</p>
        <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-label)' }}>O link pode estar incorreto ou o perfil foi desativado.</p>
        <Link to="/" style={{ color: 'var(--petrol)', textDecoration: 'none', fontSize: 'var(--text-label)' }}>← Página inicial</Link>
      </div>
    );
  }

  const serviceCategories = [...new Set(PRO_SERVICES.map(s => s.category))];

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-base)', color: 'var(--text-primary)' }}>
      {/* Hero */}
      <div style={{ background: 'linear-gradient(135deg, var(--bg-elevated) 0%, var(--bg-base) 100%)', borderBottom: '1px solid var(--border-subtle)', padding: 'clamp(2rem,6vw,4rem) 1.5rem' }}>
        <div style={{ maxWidth: '720px', margin: '0 auto', textAlign: 'center' }}>
          {ap.photo_url && (
            <img
              src={ap.photo_url}
              alt={ap.professional_name}
              style={{ width: '96px', height: '96px', borderRadius: '50%', objectFit: 'cover', border: '3px solid var(--petrol)', marginBottom: '1.25rem' }}
            />
          )}
          {!ap.photo_url && (
            <div style={{ width: '80px', height: '80px', borderRadius: '50%', background: 'var(--bg-elevated)', border: '2px solid var(--petrol)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1.25rem', fontSize: '1.8rem' }}>
              ✦
            </div>
          )}
          <h1 style={{ margin: '0 0 0.4rem', fontFamily: 'var(--font-display)', fontSize: 'clamp(1.6rem,5vw,2.2rem)', color: 'var(--text-primary)' }}>
            {ap.professional_name}
          </h1>
          {ap.title && (
            <p style={{ margin: '0 0 1rem', color: 'var(--petrol)', fontSize: 'var(--text-label)', fontWeight: 500 }}>{ap.title}</p>
          )}
          {ap.years_experience != null && (
            <p style={{ margin: '0 0 1rem', color: 'var(--text-muted)', fontSize: 'var(--text-caption)' }}>
              {ap.years_experience} anos de experiência
            </p>
          )}
          {ap.bio && (
            <p style={{ margin: '0 0 1.5rem', color: 'var(--text-secondary)', fontSize: 'var(--text-label)', lineHeight: 1.7, maxWidth: '560px', marginLeft: 'auto', marginRight: 'auto' }}>
              {ap.bio}
            </p>
          )}

          {/* Specialties */}
          {ap.specialties && ap.specialties.length > 0 && (
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', justifyContent: 'center', marginBottom: '1.5rem' }}>
              {ap.specialties.map(s => (
                <span key={s} style={{ background: 'rgba(196,154,34,0.12)', border: '1px solid rgba(196,154,34,0.3)', borderRadius: '20px', padding: '3px 12px', fontSize: '12px', color: 'var(--petrol)' }}>
                  {s}
                </span>
              ))}
            </div>
          )}

          {/* Contact Buttons */}
          <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center', flexWrap: 'wrap' }}>
            {ap.show_whatsapp && ap.whatsapp && (
              <a
                href={`https://wa.me/${ap.whatsapp.replace(/\D/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: '#25D366', color: '#fff', textDecoration: 'none', padding: '0.6rem 1.25rem', borderRadius: '8px', fontWeight: 600, fontSize: 'var(--text-label)' }}
              >
                💬 WhatsApp
              </a>
            )}
            {ap.show_email && ap.contact_email && (
              <a
                href={`mailto:${ap.contact_email}`}
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: 'var(--bg-elevated)', color: 'var(--text-primary)', textDecoration: 'none', padding: '0.6rem 1.25rem', borderRadius: '8px', fontWeight: 600, fontSize: 'var(--text-label)', border: '1px solid var(--border-subtle)' }}
              >
                ✉ E-mail
              </a>
            )}
            {ap.website && (
              <a
                href={ap.website}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: 'var(--bg-elevated)', color: 'var(--text-primary)', textDecoration: 'none', padding: '0.6rem 1.25rem', borderRadius: '8px', fontWeight: 600, fontSize: 'var(--text-label)', border: '1px solid var(--border-subtle)' }}
              >
                🌐 Website
              </a>
            )}
            {ap.instagram && (
              <a
                href={`https://instagram.com/${ap.instagram.replace('@', '')}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', background: 'var(--bg-elevated)', color: 'var(--text-primary)', textDecoration: 'none', padding: '0.6rem 1.25rem', borderRadius: '8px', fontWeight: 600, fontSize: 'var(--text-label)', border: '1px solid var(--border-subtle)' }}
              >
                📸 Instagram
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Services */}
      <div style={{ maxWidth: '720px', margin: '0 auto', padding: '2.5rem 1.5rem' }}>
        <h2 style={{ margin: '0 0 1.5rem', textAlign: 'center', fontFamily: 'var(--font-display)', fontSize: 'clamp(1.1rem,3vw,1.4rem)', color: 'var(--text-primary)' }}>
          Serviços disponíveis
        </h2>

        {serviceCategories.map(cat => {
          const catServices = PRO_SERVICES.filter(s => s.category === cat);
          return (
            <div key={cat} style={{ marginBottom: '2rem' }}>
              <p style={{ margin: '0 0 0.75rem', fontSize: 'var(--text-caption)', color: 'var(--petrol)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{cat}</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: '0.5rem' }}>
                {catServices.map(s => (
                  <div key={s.slug} style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', borderRadius: '8px', padding: '0.75rem 1rem' }}>
                    <p style={{ margin: 0, fontSize: 'var(--text-label)', color: 'var(--text-primary)', fontWeight: 500 }}>{s.name}</p>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div style={{ borderTop: '1px solid var(--border-subtle)', padding: '1.5rem', textAlign: 'center' }}>
        <p style={{ margin: 0, fontSize: 'var(--text-caption)', color: 'var(--text-muted)' }}>
          Perfil criado com{' '}
          <Link to="/" style={{ color: 'var(--petrol)', textDecoration: 'none' }}>Professya</Link>
          {' '}✦
        </p>
      </div>
    </div>
  );
}
